import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Camera,
    Upload,
    ShieldCheck,
    MapPin,
    ArrowRight,
    CheckCircle,
    AlertCircle
} from 'lucide-react';

const ProviderOnboarding = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        category: '',
        area: '',
        photo: null,
        idProof: null,
        selfie: null
    });

    const categories = ['Drivers', 'Chefs', 'Hairstylists/Barbers', 'Private Tutors', 'Nannies', 'Pharmacists'];
    const areas = ['Victoria Island', 'Ikoyi', 'Lekki', 'Ikeja', 'Ajah', 'Yaba', 'Surulere'];

    const handleNext = () => setStep(step + 1);

    const handleFinish = () => {
        alert("Onboarding complete! Your profile is now 'Pending Review' by our Admin.");
        navigate('/');
    };

    return (
        <div className="provider-onboarding animate-fade">
            <div className="onboard-header">
                <h1>Become a Provider</h1>
                <p>Join the EZ Naija network of verified professionals.</p>
            </div>

            <div className="onboard-steps-indicator">
                {[1, 2, 3, 4].map(idx => (
                    <div key={idx} className={`step-circle ${step >= idx ? 'active' : ''}`}>
                        {step > idx ? <CheckCircle size={14} /> : idx}
                    </div>
                ))}
            </div>

            <div className="onboard-content card">
                {step === 1 && (
                    <div className="onboard-section">
                        <h3>Service Details</h3>
                        <div className="form-group-o">
                            <label>Select Category</label>
                            <select onChange={(e) => setFormData({ ...formData, category: e.target.value })}>
                                <option value="">Select Category</option>
                                {categories.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div className="form-group-o">
                            <label>Service Area (Primary)</label>
                            <select onChange={(e) => setFormData({ ...formData, area: e.target.value })}>
                                <option value="">Select Area</option>
                                {areas.map(a => <option key={a} value={a}>{a}</option>)}
                            </select>
                        </div>
                        <button className="primary-btn full-width" disabled={!formData.category} onClick={handleNext}>Continue</button>
                    </div>
                )}

                {step === 2 && (
                    <div className="onboard-section">
                        <h3>Profile Photo</h3>
                        <p className="hint">This is required for customer visibility.</p>
                        <div className="upload-box" onClick={() => setFormData({ ...formData, photo: 'mock_uploaded' })}>
                            {formData.photo ? <CheckCircle size={40} color="var(--primary-green)" /> : <Camera size={40} color="#ccc" />}
                            <span>{formData.photo ? "Photo Uploaded" : "Tap to upload profile photo"}</span>
                        </div>
                        <button className="primary-btn full-width" disabled={!formData.photo} onClick={handleNext}>Continue</button>
                    </div>
                )}

                {step === 3 && (
                    <div className="onboard-section">
                        <h3>Identity Verification (KYC)</h3>
                        <p className="hint">Upload a valid ID card and a selfie for verification.</p>
                        <div className="kyc-uploads">
                            <div className="upload-mini" onClick={() => setFormData({ ...formData, idProof: 'done' })}>
                                <Upload size={20} />
                                <span>ID Card {formData.idProof && '✓'}</span>
                            </div>
                            <div className="upload-mini" onClick={() => setFormData({ ...formData, selfie: 'done' })}>
                                <Camera size={20} />
                                <span>Selfie {formData.selfie && '✓'}</span>
                            </div>
                        </div>
                        <div className="verification-notice">
                            <ShieldCheck size={16} />
                            <span>We use bank-grade encryption to protect your data.</span>
                        </div>
                        <button className="primary-btn full-width" disabled={!formData.idProof || !formData.selfie} onClick={handleNext}>Start Liveness Check</button>
                    </div>
                )}

                {step === 4 && (
                    <div className="onboard-section">
                        <h3>Liveness Check</h3>
                        <div className="liveness-circle">
                            <div className="face-outline"></div>
                        </div>
                        <p className="instr">Position your face in the circle and blink once.</p>
                        <button className="primary-btn full-width" onClick={handleFinish}>Complete Setup</button>
                    </div>
                )}
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
        .provider-onboarding { padding: 20px; padding-bottom: 100px; }
        .onboard-header { margin-bottom: 30px; }
        .onboard-header h1 { font-size: 24px; }
        .onboard-header p { font-size: 14px; color: var(--text-light); }

        .onboard-steps-indicator { display: flex; justify-content: space-between; position: relative; margin-bottom: 30px; padding: 0 20px; }
        .onboard-steps-indicator::before { content: ''; position: absolute; top: 15px; left: 40px; right: 40px; height: 2px; background: var(--border-gray); z-index: 1; }
        .step-circle { width: 32px; height: 32px; border-radius: 50%; background: white; border: 2px solid var(--border-gray); display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; color: var(--text-light); z-index: 2; position: relative; }
        .step-circle.active { border-color: var(--primary-green); color: var(--primary-green); }
        .step-circle svg { color: var(--primary-green); }

        .onboard-content { padding: 24px; }
        .onboard-section h3 { margin-bottom: 8px; font-size: 18px; }
        .hint { font-size: 13px; color: var(--text-light); margin-bottom: 24px; }
        
        .upload-box { height: 160px; border: 2px dashed var(--border-gray); border-radius: 12px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; margin-bottom: 24px; cursor: pointer; transition: 0.2s; }
        .upload-box:hover { border-color: var(--primary-green); background: var(--light-green); }
        .upload-box span { font-size: 14px; font-weight: 600; color: var(--text-light); }
        
        .kyc-uploads { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 20px; }
        .upload-mini { height: 100px; border: 1px solid var(--border-gray); border-radius: 10px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; cursor: pointer; }
        .upload-mini span { font-size: 12px; font-weight: 600; }
        
        .verification-notice { display: flex; align-items: center; gap: 8px; font-size: 11px; color: var(--text-light); margin-bottom: 24px; background: #f8fafc; padding: 10px; border-radius: 6px; }

        .liveness-circle { width: 180px; height: 180px; border-radius: 50%; border: 4px solid var(--primary-green); margin: 0 auto 24px; position: relative; overflow: hidden; background: #eee; }
        .face-outline { position: absolute; top: 15%; left: 15%; right: 15%; bottom: 15%; border: 2px dashed rgba(0,0,0,0.2); border-radius: 40% 40% 50% 50%; }
        .instr { text-align: center; font-weight: 600; font-size: 14px; margin-bottom: 24px; }
        
        .full-width { width: 100%; }
        .form-group-o { margin-bottom: 20px; }
        .form-group-o label { font-size: 12px; font-weight: 700; color: var(--text-dark); margin-bottom: 6px; display: block; text-transform: uppercase; }
        .form-group-o select { width: 100%; padding: 14px; border: 1px solid var(--border-gray); border-radius: 8px; background: white; -webkit-appearance: none; }
      `}} />
        </div>
    );
};

export default ProviderOnboarding;
